import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../Components/SEO/SEO";
import blogService from "../../Services/blogService";
import type { Blog } from "../../Types/blog.types";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarIcon from "@mui/icons-material/Star";
import ScheduleIcon from "@mui/icons-material/Schedule";
import "./Blogs.css";

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [featuredBlogs, setFeaturedBlogs] = useState<Blog[]>([]);
  const [popularBlogs, setPopularBlogs] = useState<Blog[]>([]);
  const [recentBlogs, setRecentBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalBlogs, setTotalBlogs] = useState<number>(0);
  const blogsPerPage = 9;

  const categories = [
    "all",
    "technology",
    "business",
    "innovation",
    "healthcare",
  ];

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedCategory]);

  useEffect(() => {
    // Reset to page 1 when search query changes
    if (searchQuery) {
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const loadAllData = async (): Promise<void> => {
    try {
      setLoading(true);

      // Build query parameters
      const params: any = {
        published: true,
        page: currentPage,
        limit: blogsPerPage,
        sortBy: "publishedAt",
        sortOrder: "desc",
      };

      if (selectedCategory !== "all") {
        params.category = selectedCategory;
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Load main blogs with pagination
      const blogsResponse = (await blogService.getAllBlogs(params)) as any;

      // Handle response structure
      if (blogsResponse.success) {
        // Check for paginated response
        if (
          blogsResponse.data?.data &&
          Array.isArray(blogsResponse.data.data)
        ) {
          const { data, pagination } = blogsResponse.data;
          setBlogs(data);
          setTotalPages(pagination.totalPages);
          setTotalBlogs(pagination.total);
          setCurrentPage(pagination.page);
        }
        // Check for direct array
        else if (Array.isArray(blogsResponse.data)) {
          setBlogs(blogsResponse.data);
          setTotalPages(1);
          setTotalBlogs(blogsResponse.data.length);
        }
      }

      // Load sidebar data (only once, not on every page change)
      if (currentPage === 1) {
        loadSidebarData();
      }
    } catch (error) {
      console.error("Error loading blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSidebarData = async (): Promise<void> => {
    try {
      // Load featured blogs
      const featuredResponse = await blogService.getFeaturedBlogs(3);
      if (featuredResponse.success && Array.isArray(featuredResponse.data)) {
        setFeaturedBlogs(featuredResponse.data);
      }

      // Load popular blogs
      const popularResponse = await blogService.getPopularBlogs(5);
      if (popularResponse.success && Array.isArray(popularResponse.data)) {
        setPopularBlogs(popularResponse.data);
      }

      // Load recent blogs
      const recentResponse = await blogService.getRecentBlogs(5);
      if (recentResponse.success && Array.isArray(recentResponse.data)) {
        setRecentBlogs(recentResponse.data);
      }
    } catch (error) {
      console.error("Error loading sidebar data:", error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadAllData();
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getAuthorName = (author: any): string => {
    if (!author) return "Lico Team";
    if (typeof author === "string") return author;
    if (typeof author === "object" && author.name) return author.name;
    return "Lico Team";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Generate avatar URL from title
  const getAvatarUrl = (title: string) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      title
    )}&size=400&background=FF9933&color=fff&bold=true&font-size=0.4`;
  };

  if (loading && currentPage === 1) {
    return (
      <div className="blog-page">
        <SEO />
        <div className="blog-loading-container">
          <div className="blog-loading-content">
            <div className="loading-spinner-large"></div>
            <h2>Loading Articles...</h2>
            <p>Please wait while we fetch the latest content</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <SEO />

      {/* Hero Section - FAQ STYLE */}
      <section className="blogs-hero">
        <div className="container">
          <div className="blogs-hero-content">
            <h1>Latest News & Insights</h1>
            <p>
              Stay updated with the latest trends, tips, and stories from LICO
              Technologies
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="blog-filters">
        <div className="container">
          <form onSubmit={handleSearch} className="search-box">
            <SearchIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-submit-btn">
              Search
            </button>
          </form>

          <div className="category-filters">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-btn ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => handleCategoryChange(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {(searchQuery || selectedCategory !== "all") && (
            <div className="active-filters">
              <span>Active filters:</span>
              {searchQuery && (
                <span className="filter-tag">
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery("")}>×</button>
                </span>
              )}
              {selectedCategory !== "all" && (
                <span className="filter-tag">
                  Category: {selectedCategory}
                  <button onClick={() => handleCategoryChange("all")}>×</button>
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="blog-content">
        <div className="container">
          <div className="blog-layout">
            {/* Main Blog Grid */}
            <div className="blog-main">
              {loading ? (
                <div className="loading-grid">
                  <div className="spinner"></div>
                  <p>Loading articles...</p>
                </div>
              ) : blogs.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">📝</div>
                  <h2>No Articles Found</h2>
                  <p>
                    {searchQuery
                      ? `No articles match "${searchQuery}". Try different keywords.`
                      : selectedCategory !== "all"
                      ? `No articles available in the ${selectedCategory} category.`
                      : "No articles available at the moment. Check back soon!"}
                  </p>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("all");
                      setCurrentPage(1);
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="results-info">
                    <p>
                      Showing {blogs.length} of {totalBlogs} articles
                      {selectedCategory !== "all" && ` in ${selectedCategory}`}
                    </p>
                  </div>

                  <div className="blog-grid">
                    {blogs.map((blog) => (
                      <Link
                        key={blog._id}
                        to={`/blog/${blog.slug}`}
                        className="blog-card"
                      >
                        <div className="blog-card-image">
                          <img
                            src={blog.featuredImage || getAvatarUrl(blog.title)}
                            alt={blog.title}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getAvatarUrl(blog.title);
                            }}
                          />
                          <div className="blog-card-overlay">
                            <span className="category-badge">
                              {blog.category}
                            </span>
                          </div>
                        </div>
                        <div className="blog-card-content">
                          <h3 className="blog-card-title">{blog.title}</h3>
                          <p className="blog-card-excerpt">{blog.excerpt}</p>

                          <div className="blog-card-meta">
                            <div className="meta-item">
                              <PersonIcon />
                              <span>{getAuthorName(blog.author)}</span>
                            </div>
                            <div className="meta-item">
                              <AccessTimeIcon />
                              <span>{blog.readTime}</span>
                            </div>
                          </div>

                          <div className="blog-card-stats">
                            <div className="stat-item">
                              <VisibilityIcon />
                              <span>{blog.views}</span>
                            </div>
                            <div className="stat-item">
                              <FavoriteIcon />
                              <span>{blog.likes}</span>
                            </div>
                          </div>

                          <div className="blog-card-date">
                            {formatDate(blog.publishedAt)}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                      >
                        ← Previous
                      </button>

                      <div className="pagination-numbers">
                        {Array.from(
                          { length: Math.min(totalPages, 7) },
                          (_, i) => {
                            let pageNumber: number;
                            if (totalPages <= 7) {
                              pageNumber = i + 1;
                            } else if (currentPage <= 4) {
                              pageNumber = i + 1;
                            } else if (currentPage >= totalPages - 3) {
                              pageNumber = totalPages - 6 + i;
                            } else {
                              pageNumber = currentPage - 3 + i;
                            }

                            return (
                              <button
                                key={pageNumber}
                                className={`pagination-number ${
                                  currentPage === pageNumber ? "active" : ""
                                }`}
                                onClick={() => handlePageChange(pageNumber)}
                                disabled={loading}
                              >
                                {pageNumber}
                              </button>
                            );
                          }
                        )}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar */}
            <aside className="blog-sidebar">
              {/* Featured Posts */}
              {featuredBlogs.length > 0 && (
                <div className="sidebar-widget featured-widget">
                  <div className="widget-header">
                    <StarIcon className="widget-icon" />
                    <h3>Featured Articles</h3>
                  </div>
                  <div className="widget-content">
                    {featuredBlogs.map((blog) => (
                      <Link
                        key={blog._id}
                        to={`/blog/${blog.slug}`}
                        className="sidebar-post"
                      >
                        <div className="sidebar-post-image">
                          <img
                            src={blog.featuredImage || getAvatarUrl(blog.title)}
                            alt={blog.title}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getAvatarUrl(blog.title);
                            }}
                          />
                        </div>
                        <div className="sidebar-post-content">
                          <h4>{truncateText(blog.title, 60)}</h4>
                          <div className="sidebar-post-meta">
                            <AccessTimeIcon />
                            <span>{blog.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Posts */}
              {popularBlogs.length > 0 && (
                <div className="sidebar-widget popular-widget">
                  <div className="widget-header">
                    <TrendingUpIcon className="widget-icon" />
                    <h3>Popular Articles</h3>
                  </div>
                  <div className="widget-content">
                    {popularBlogs.map((blog, index) => (
                      <Link
                        key={blog._id}
                        to={`/blog/${blog.slug}`}
                        className="sidebar-list-item"
                      >
                        <span className="item-number">{index + 1}</span>
                        <div className="item-content">
                          <h4>{truncateText(blog.title, 50)}</h4>
                          <div className="item-stats">
                            <span>
                              <VisibilityIcon /> {blog.views}
                            </span>
                            <span>
                              <FavoriteIcon /> {blog.likes}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Posts */}
              {recentBlogs.length > 0 && (
                <div className="sidebar-widget recent-widget">
                  <div className="widget-header">
                    <ScheduleIcon className="widget-icon" />
                    <h3>Recent Articles</h3>
                  </div>
                  <div className="widget-content">
                    {recentBlogs.map((blog) => (
                      <Link
                        key={blog._id}
                        to={`/blog/${blog.slug}`}
                        className="sidebar-list-item"
                      >
                        <div className="item-content">
                          <h4>{truncateText(blog.title, 50)}</h4>
                          <div className="item-date">
                            {formatDate(blog.publishedAt)}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blogs;
